import { CommonModule } from '@angular/common';
import { Component, inject } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { finalize } from 'rxjs';
import { EmpresaUsuariosService } from '../../../core/services/empresa-usuarios.service';
import {
  EmpresaUsuario,
  EmpresaUsuarioForm,
  EmpresaUsuarioRole,
  EmpresaUsuariosResumen,
} from '../../../core/models/empresa-usuario.model';
import { PageHeaderComponent } from '../../../shared/page-header/page-header';
import { SummaryCardComponent } from '../../../shared/summary-card/summary-card';
import { LoadingComponent } from '../../../shared/loading/loading';
import { UiButtonComponent } from '../../../shared/ui-button/ui-button';
import { SelectComponent, SelectOption } from '../../../shared/select/select';
import { DataTablePaginado } from '../../../shared/data-table-paginado/data-table-paginado';
import {
  TableActionEvent,
  TableColumn,
  TablePageEvent,
  TableSortEvent,
} from '../../../shared/data-table-paginado/data-table-paginado.models';
import { ModalShellComponent } from '../../../shared/modal-shell/modal-shell';
import {
  ConfirmationModalComponent,
  ConfirmationStatus,
} from '../../../shared/confirmation-modal/confirmation-modal';
import { CardListComponent } from '../../../shared/card-list/card-list';
import { MobileService } from '../../../core/services/mobile.service';
import { AuthService } from '../../../core/services/auth.service';

@Component({
  selector: 'app-empresa-usuarios',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    PageHeaderComponent,
    SummaryCardComponent,
    LoadingComponent,
    UiButtonComponent,
    SelectComponent,
    DataTablePaginado,
    ModalShellComponent,
    ConfirmationModalComponent,
    CardListComponent,
  ],
  templateUrl: './usuarios.html',
  styleUrl: './usuarios.css',
})
export class EmpresaUsuariosComponent {
  private readonly service = inject(EmpresaUsuariosService);
  private readonly authService = inject(AuthService);
  protected readonly mobileService = inject(MobileService);
  protected usuarios: EmpresaUsuario[] = [];
  protected roles: EmpresaUsuarioRole[] = [];
  protected resumenData: EmpresaUsuariosResumen | null = null;
  protected loading = true;
  protected saving = false;
  protected page = 1;
  protected pageSize = 10;
  protected totalItems = 0;
  protected sortBy: string | undefined;
  protected sortDirection: 'asc' | 'desc' | undefined;
  protected search = '';
  protected status = 'Activo';
  protected error = '';
  protected formError = '';
  protected passwordError = '';
  protected message = '';
  protected showForm = false;
  protected showPasswordModal = false;
  protected showStatusConfirmation = false;
  protected pendingStatusUser: EmpresaUsuario | null = null;
  protected passwordValue = '';
  protected confirmationStatus: ConfirmationStatus = 'active';
  protected processingStatus = false;
  protected editing: EmpresaUsuario | null = null;
  protected form: EmpresaUsuarioForm = this.emptyForm();
  protected readonly statusOptions: SelectOption[] = [
    { value: 'Todos', label: 'Todos' },
    { value: 'Activo', label: 'Activos' },
    { value: 'Inactivo', label: 'Inactivos' },
  ];
  protected readonly columns: TableColumn<EmpresaUsuario>[] = [
    { key: 'nombre', label: 'Nombre', sortable: true },
    { key: 'usuario', label: 'Usuario', sortable: true },
    {
      key: 'rolesUsuario',
      label: 'Roles',
      formatter: (_, row) => this.roleLabel(row),
    },
    { key: 'fechaCreacion', label: 'Registro', type: 'date', sortable: true },
    {
      key: 'estado',
      label: 'Estado',
      type: 'badge',
      sortable: true,
      align: 'center',
      badgeClass: (value) => this.getBadgeClass(value),
    },
    {
      key: '__actions',
      label: 'Acciones',
      type: 'actions',
      align: 'center',
      actions: [
        {
          id: 'edit',
          label: 'Editar',
          icon: 'fa-solid fa-pen',
          ariaLabel: 'Editar usuario',
          disabled: (row) => this.isCurrentUser(row),
        },
        {
          id: 'toggle',
          label: 'Cambiar estado',
          icon: 'fa-solid fa-power-off',
          ariaLabel: 'Cambiar estado',
          disabled: (row) => this.isCurrentUser(row),
        },
        {
          id: 'password',
          label: 'Contraseña',
          icon: 'fa-solid fa-key',
          ariaLabel: 'Restablecer contraseña',
        },
      ],
    },
  ];

  constructor() {
    this.load();
  }

  protected load(): void {
    this.loading = true;
    this.service.resumen().subscribe({ next: (r) => (this.resumenData = r.data ?? null) });
    this.service.roles().subscribe({ next: (r) => (this.roles = r.data ?? []) });
    this.loadUsers();
  }

  protected loadUsers(): void {
    this.service
      .paginado(this.page, this.pageSize, this.search, this.status, this.sortBy, this.sortDirection)
      .pipe(finalize(() => (this.loading = false)))
      .subscribe({
        next: (r) => {
          this.usuarios = r.data?.items ?? [];
          this.totalItems = r.data?.totalItems ?? 0;
        },
        error: (e) => (this.error = e?.error?.message ?? 'No se pudieron cargar los usuarios.'),
      });
  }

  protected nueva(): void {
    this.editing = null;
    this.form = this.emptyForm();
    this.formError = '';
    this.showForm = true;
  }

  protected editar(usuario: EmpresaUsuario): void {
    if (this.isCurrentUser(usuario)) return;

    this.editing = usuario;
    this.form = {
      nombre: usuario.nombre,
      usuario: usuario.usuario,
      rolIds: usuario.rolesUsuario.map((role) => role.rolId),
    };
    this.formError = '';
    this.showForm = true;
  }

  protected guardar(): void {
    if (
      !this.form.nombre.trim() ||
      !this.form.usuario.trim() ||
      (!this.editing && !this.form.password)
    ) {
      this.formError = 'Completa nombre, usuario y contraseña.';
      return;
    }
    this.saving = true;
    const call = this.editing
      ? this.service.actualizar(this.editing.usuarioId, this.form)
      : this.service.crear(this.form);
    call.pipe(finalize(() => (this.saving = false))).subscribe({
      next: () => {
        this.message = this.editing ? 'Usuario actualizado.' : 'Usuario creado.';
        this.showForm = false;
        this.load();
      },
      error: (e) => (this.formError = e?.error?.message ?? 'No se pudo guardar el usuario.'),
    });
  }

  protected toggle(usuario: EmpresaUsuario): void {
    if (this.isCurrentUser(usuario)) return;

    this.pendingStatusUser = usuario;
    this.confirmationStatus = 'active';
    this.showStatusConfirmation = true;
  }

  protected confirmStatusChange(): void {
    if (!this.pendingStatusUser || this.processingStatus) return;
    this.processingStatus = true;
    const call =
      this.pendingStatusUser.estado === 'ACTIVO'
        ? this.service.desactivar(this.pendingStatusUser.usuarioId)
        : this.service.activar(this.pendingStatusUser.usuarioId);
    call.subscribe({
      next: () => {
        this.message = 'Estado actualizado.';
        this.confirmationStatus = 'success';
        this.processingStatus = false;
        this.load();
      },
      error: (e) => {
        this.error = e?.error?.message ?? 'No se pudo actualizar el estado.';
        this.confirmationStatus = 'error';
        this.processingStatus = false;
      },
    });
  }

  protected closeStatusConfirmation(): void {
    if (this.processingStatus) return;
    this.showStatusConfirmation = false;
    this.pendingStatusUser = null;
    this.confirmationStatus = 'active';
  }

  protected resetPassword(usuario: EmpresaUsuario): void {
    this.pendingStatusUser = usuario;
    this.passwordValue = '';
    this.passwordError = '';
    this.showPasswordModal = true;
  }

  protected savePassword(): void {
    if (!this.pendingStatusUser || this.passwordValue.length < 8) {
      this.passwordError = 'La contraseña debe tener al menos 8 caracteres.';
      return;
    }
    this.saving = true;
    this.service
      .restablecerPassword(this.pendingStatusUser.usuarioId, this.passwordValue)
      .pipe(finalize(() => (this.saving = false)))
      .subscribe({
        next: () => {
          this.message = 'Contraseña actualizada.';
          this.showPasswordModal = false;
          this.pendingStatusUser = null;
          this.passwordValue = '';
        },
        error: (e) =>
          (this.passwordError = e?.error?.message ?? 'No se pudo actualizar la contraseña.'),
      });
  }

  protected closePasswordModal(): void {
    if (this.saving) return;
    this.showPasswordModal = false;
    this.pendingStatusUser = null;
    this.passwordValue = '';
    this.passwordError = '';
  }

  protected toggleRole(roleId: string, event: Event): void {
    const checked = (event.target as HTMLInputElement).checked;
    this.form.rolIds = checked
      ? [...this.form.rolIds, roleId]
      : this.form.rolIds.filter((id) => id !== roleId);
  }

  protected searchUsers(): void {
    this.page = 1;
    this.loadUsers();
  }
  protected onPageChange(event: TablePageEvent): void {
    this.page = event.page;
    this.pageSize = event.pageSize;
    this.loadUsers();
  }
  protected onPageSizeChange(event: TablePageEvent): void {
    this.page = 1;
    this.pageSize = event.pageSize;
    this.loadUsers();
  }
  protected onSort(event: TableSortEvent): void {
    this.page = 1;
    this.sortBy = event.column ?? undefined;
    this.sortDirection = event.direction ?? undefined;
    this.loadUsers();
  }
  protected onAction(event: TableActionEvent<EmpresaUsuario>): void {
    if (event.action === 'edit') this.editar(event.row);
    if (event.action === 'toggle') this.toggle(event.row);
    if (event.action === 'password') this.resetPassword(event.row);
  }
  protected changePage(delta: number): void {
    if (this.page + delta < 1 || this.page + delta > Math.ceil(this.totalItems / this.pageSize))
      return;
    this.page += delta;
    this.loadUsers();
  }
  protected closeForm(): void {
    if (!this.saving) this.showForm = false;
    this.formError = '';
  }
  protected get totalPages(): number {
    return Math.max(1, Math.ceil(this.totalItems / this.pageSize));
  }
  protected roleLabel(usuario: EmpresaUsuario): string {
    return usuario.rolesUsuario.map((role) => role.rolCodigo).join(', ') || 'Sin rol';
  }
  protected isCurrentUser(usuario: EmpresaUsuario): boolean {
    const currentUsername = this.authService.currentUser()?.usuario;
    if (!currentUsername) return false;

    return this.normalizeUsername(usuario.usuario) === this.normalizeUsername(currentUsername);
  }

  private normalizeUsername(username: string): string {
    return username.trim().toLowerCase();
  }
  private emptyForm(): EmpresaUsuarioForm {
    return { nombre: '', usuario: '', password: '', rolIds: [] };
  }

  protected getBadgeClass(status: string): string {
    return status.trim().toLowerCase() === 'activo' ? 'badge-active' : 'badge-inactive';
  }
}
